import {MdbsCore} from "@/api/tsclient";
import {deepCopy} from "@/auxillary";
import {v4 as uuidv4} from 'uuid';
import {translate} from "@/auxillary/translation";
import InstrumentContextDto = MdbsCore.InstrumentContextDto;
import InstrumentDefinitionDto = MdbsCore.InstrumentDefinitionDto;
import DataFieldShortDto = MdbsCore.DataFieldShortDto;
import {resolveConstraintExpression} from "@/auxillary/constraint.expression.resolver";
import VisitInstrumentDefinitionDto = MdbsCore.VisitInstrumentDefinitionDto;
import StudyDefinitionStandaloneInstrumentDto = MdbsCore.StudyDefinitionStandaloneInstrumentDto;
import RuleDefinitionDto = MdbsCore.RuleDefinitionDto;
import {DEFINITIONTYPE_EDITABILITY, DEFINITIONTYPE_HINT, DEFINITIONTYPE_VISIBILITY} from "@/auxillary/rules";
import {studyPatientContext} from "@/store";

export default class InstrumentContextCache {

    private readonly _definition: Readonly<InstrumentDefinitionDto>;
    private readonly instrumentContext: Readonly<InstrumentContextDto> | undefined;
    private readonly _isDeleted: boolean = false;
    private readonly _isDefinitionDeleted: boolean = false;
    private readonly _order: number = 0;
    private readonly _temporaryId: string = "";
    private readonly _isVisible: boolean = true;
    private readonly _isLocked: boolean = false;
    private readonly _fields: DataFieldShortDto[] = [];
    private readonly _tags: any = {};
    private readonly _state: string="new";
    private readonly _parentLinkRules: Readonly<RuleDefinitionDto[]> = [];
    private readonly _visibilityRule: Readonly<RuleDefinitionDto> | undefined;
    private readonly _hintRules:  Array<Readonly<RuleDefinitionDto>> | undefined;
    private readonly _editabilityRules:  Array<Readonly<RuleDefinitionDto>> | undefined;
    private _brokenEditabilityRule:  RuleDefinitionDto | undefined;

    constructor(
        definition: Readonly<InstrumentDefinitionDto>,
        instrument: Readonly<InstrumentContextDto> | undefined,
        parentLinkDefinition : Readonly<VisitInstrumentDefinitionDto> |Readonly<StudyDefinitionStandaloneInstrumentDto>) {

        this._definition = Object.freeze(definition);
        this._displayName = translate(this.displayKey);
        this._parentLinkRules = parentLinkDefinition.rules ?? [];
        this._visibilityRule = this._parentLinkRules.find(r => r.definitionType == DEFINITIONTYPE_VISIBILITY && !r.isDeleted)
        this._hintRules = this._parentLinkRules.filter(r => r.definitionType == DEFINITIONTYPE_HINT && !r.isDeleted)
        this._editabilityRules = this._parentLinkRules.filter(r => r.definitionType == DEFINITIONTYPE_EDITABILITY && !r.isDeleted)
        this._isDefinitionDeleted = (definition.isDeleted || parentLinkDefinition.isDeleted) ?? false;

        if (instrument === undefined) {
            this.instrumentContext = undefined;
            this._order = definition.order ?? 0;
            this._temporaryId = uuidv4().toString();
            return;
        }

        this.instrumentContext = instrument.clone();
        this._isVisible = true;
        this._isLocked = instrument.locked ?? false;
        this._fields = deepCopy(instrument.fields ?? [])
        this._tags = deepCopy(instrument.tags)
        this._order = instrument.order ?? 0;
        this._isDeleted = instrument.isDeleted ?? false;
        this._state = instrument.state ?? "";
    }

    public get id():string {
        return this.instrumentContext?.id ?? this._temporaryId;
    }

    public get identifier() {
        return this._definition.identifier ?? "";
    }

    public get displayKey() {
        return this._definition.displayKey ?? "";
    }

    public get state() {
        return this._state ?? "";
    }

    private _displayName?: string;

    public get displayName() {
        if (this._displayName === undefined) {
            this._displayName = translate(this.displayKey);
        }
        return this._displayName;
    }

    get definitionId() {
        return this._definition?.id;
    }

    get definition() {
        return this._definition;
    }


    public get isNew() {
        return this.instrumentContext === undefined;
    }

    public hasMissingData(){
        return this._state === "missing_data";
    }

    public isComplete(){
        return this._state === "complete";
    }

    get isDeleted(): boolean {
        return this._isDeleted;
    }

    get isDefinitionDeleted(): boolean {
        return this._isDefinitionDeleted;
    }

    get order(): number {
        return this._order;
    }

    get temporaryId(): string {
        return this._temporaryId;
    }

    get isVisible(){
        if (this.isDeleted || this.isDefinitionDeleted)
            return false;

        const visibilityRule = this._parentLinkRules.find(r => r.definitionType == "Visibility"  && !r.isDeleted)
        if (visibilityRule !== undefined) {
            return resolveConstraintExpression(visibilityRule.constraintExpression);
        }
        return true;
    }

    get isEditable() {
        this._brokenEditabilityRule = undefined;
        if (this.isDeleted || this._definition.isDeleted)
            return false;

        if (this._editabilityRules === undefined || this._editabilityRules.length == 0)
            return true;

        const visitCache = studyPatientContext.visits.find(v => v.instruments.some(i => i.id == this.instrumentContext?.id || i.temporaryId == this.instrumentContext?.id));
        const brokenRules = this._editabilityRules.filter(rule =>
            !resolveConstraintExpression(rule.constraintExpression, undefined, visitCache));

        if (brokenRules.length == 0)
            return true;

        this._brokenEditabilityRule = brokenRules[0];
        return false;
    }

    get brokenEditableRule() {
        return this._brokenEditabilityRule;
    }

    get notEditableReason() {
        if (!this._brokenEditabilityRule)
            return "";
        return translate(this._brokenEditabilityRule?.displayKey);
    }

    public get hintRules(): Readonly<RuleDefinitionDto[]> {
        if (this._isDeleted || this._definition.isDeleted)
            return [];

        return this._hintRules ?? [];
    }

    get isLocked(): boolean {
        return this._isLocked;
    }

    get fields(): DataFieldShortDto[] {
        return this._fields;
    }

    get tags(): any {
        return this._tags;
    }
}
